/**
 * Consumer card colour palette (program-configurable).
 *
 * Stored as `programs.public_config.colorChoices` (already on the public
 * projection allowlist) — an array of `{ id, colour, cname }`. When the program
 * stores nothing, the consumer surface falls back to this FULL set, so it can
 * never silently render a partial palette again.
 */
export interface ConsumerColour {
  id: string;
  colour: string;
  cname: string;
}

export const CONSUMER_CARD_COLOURS: ConsumerColour[] = [
  { id: "kelly", colour: "#20AE36", cname: "Kelly green" },
  { id: "grape", colour: "#8834B2", cname: "Grape" },
  { id: "cobalt", colour: "#2456C8", cname: "Cobalt" },
  { id: "lemon", colour: "#FFD400", cname: "Lemon" },
  { id: "tangerine", colour: "#EE5B2A", cname: "Tangerine" },
  { id: "rose", colour: "#E05281", cname: "Rose" },
  { id: "azure", colour: "#1A80E5", cname: "Azure" },
  { id: "ink", colour: "#1C2030", cname: "Ink" },
  { id: "forest", colour: "#1E7A4C", cname: "Forest" },
  { id: "slate", colour: "#6B7280", cname: "Slate" },
  { id: "gold", colour: "#E0A521", cname: "Gold" },
  // THE red, restored. #EE1313 is the authored hex; it round-trips to
  // hsl(0 87% 50%), so the stored value is #EC1313 — the hex that serialises
  // EXACTLY to the `0 85% 50%` already stamped on existing card_requests rows.
  // (Visually identical; chosen for value-consistency with live orders.)
  // The 12-colour expansion accidentally shipped #C41112 here, which is the
  // ERROR-TEXT red token, not a card colour.
  { id: "red", colour: "#EC1313", cname: "Red" },
];

/**
 * Legacy value remap. The accidental error-red (#C41112, id "crimson") is
 * rewritten to THE red on read, so programme rows saved during the 12-colour
 * expansion line up with the `hsl(0 85% 50%)` already stamped on card_requests
 * without needing a data migration. The next config save persists the fix.
 */
const LEGACY_REMAP: Record<string, ConsumerColour> = {
  crimson: { id: "red", colour: "#EC1313", cname: "Red" },
  "#c41112": { id: "red", colour: "#EC1313", cname: "Red" },
};

function remap(c: ConsumerColour): ConsumerColour {
  return LEGACY_REMAP[c.id.toLowerCase()] ?? LEGACY_REMAP[c.colour.toLowerCase()] ?? c;
}

/** Normalise whatever the program stored into the consumer swatch shape. */
export function resolveConsumerColours(raw: unknown): ConsumerColour[] {
  if (!Array.isArray(raw) || raw.length === 0) return CONSUMER_CARD_COLOURS;
  const out: ConsumerColour[] = [];
  for (const item of raw) {
    if (typeof item === "string") {
      const known = CONSUMER_CARD_COLOURS.find(
        (c) => c.id === item || c.colour.toLowerCase() === item.toLowerCase()
      );
      out.push(remap(known ?? { id: item, colour: item, cname: item }));
    } else if (item && typeof item === "object") {
      const o = item as Record<string, any>;
      const colour = o.colour ?? o.color ?? o.hex;
      if (typeof colour === "string") {
        out.push(
          remap({
            id: String(o.id ?? colour),
            colour,
            cname: String(o.cname ?? o.name ?? colour),
          })
        );
      }
    }
  }
  return out.length ? out : CONSUMER_CARD_COLOURS;
}

/** ↔ the B2B `CardColor` shape ({id,name,hsl}) used by CustomColorsSection. */
export function toCardColors(
  palette: ConsumerColour[],
  hexToHsl: (hex: string) => string
): { id: string; name: string; hsl: string }[] {
  return palette.map((c) => ({ id: c.id, name: c.cname, hsl: hexToHsl(c.colour) }));
}

export function fromCardColors(
  colors: { id: string; name: string; hsl: string }[],
  hslToHex: (hsl: string) => string
): ConsumerColour[] {
  return colors.map((c) => ({ id: c.id, colour: hslToHex(c.hsl).toUpperCase(), cname: c.name }));
}
